import React, { useState } from 'react';
import { useGetInboxListQuery, useGetEmailListQuery } from '@/store/emailInboxApi';
import { IncomingEmailListItem } from '@/types/EmailInbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { EmailDetailModal } from './EmailDetailModal';
import { Search, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'כל הסטטוסים' },
  { value: 'pending', label: 'ממתין לסיווג' },
  { value: 'reply', label: 'ממתין לתגובת לקוח' },
  { value: 'attached', label: 'מקושר לפניה' },
  { value: 'lead', label: 'ליד' },
  { value: 'ignored', label: 'מוסתר' },
];

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  reply: 'bg-blue-100 text-blue-700',
  attached: 'bg-green-100 text-green-700',
  lead: 'bg-purple-100 text-purple-700',
  ignored: 'bg-gray-100 text-gray-500',
};

const AI_MATCHED_BY_LABELS: Record<string, string> = {
  thread_lookup:        'שרשור קיים',
  thread_lookup_open:   'שרשור פתוח',
  thread_lookup_closed: 'שרשור סגור',
  clarification_reply:  'תגובת הבהרה',
  auto_rule:            'כלל אוטומטי',
  subject_phone:        'טלפון בנושא',
  single_school:        'מערכת יחידה',
  ai_school_match:      'התאמת AI',
  template_reply:       'תגובת תבנית',
};

interface Props {
  inboxId: number | null;
  onInboxChange: (id: number | null) => void;
}

export function EmailsTab({ inboxId, onInboxChange }: Props) {
  const { data: inboxes = [] } = useGetInboxListQuery();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);

  const { data: emailPage, isLoading, isFetching, refetch } = useGetEmailListQuery({
    inboxId: inboxId ?? undefined,
    page,
    page_size: 25,
    status: statusFilter || undefined,
    search: appliedSearch || undefined,
  });

  const emails: IncomingEmailListItem[] = emailPage?.results?.data ?? [];
  const total = emailPage?.count ?? 0;
  const totalPages = Math.ceil(total / 25);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setAppliedSearch(search);
    setPage(1);
  }

  function handleStatusChange(val: string) {
    setStatusFilter(val);
    setPage(1);
  }

  function handleInboxChange(val: string) {
    onInboxChange(val === 'all' ? null : Number(val));
    setPage(1);
  }

  return (
    <div className="space-y-4" dir="rtl">
      {/* Filters bar */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1 min-w-[180px]">
          <p className="text-xs text-gray-500">תיבה</p>
          <Select value={inboxId ? String(inboxId) : 'all'} onValueChange={handleInboxChange}>
            <SelectTrigger>
              <SelectValue placeholder="כל התיבות" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">כל התיבות</SelectItem>
              {inboxes.map((inbox) => (
                <SelectItem key={inbox.id} value={String(inbox.id)}>
                  {inbox.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1 min-w-[160px]">
          <p className="text-xs text-gray-500">סטטוס</p>
          <Select value={statusFilter || 'all'} onValueChange={(v) => handleStatusChange(v === 'all' ? '' : v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value || 'all'} value={o.value || 'all'}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="חיפוש לפי נושא / שולח..."
            className="flex-1"
          />
          <Button type="submit" variant="outline" size="sm">
            <Search className="h-4 w-4" />
          </Button>
        </form>

        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          title="רענן רשימת מיילים"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Summary */}
      <p className="text-sm text-gray-500">
        {isLoading ? 'טוען...' : `${total.toLocaleString('he-IL')} מיילים`}
        {isFetching && !isLoading && ' (מעדכן...)'}
      </p>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : emails.length === 0 ? (
        <div className="text-center py-16 text-gray-400 border border-dashed rounded-lg">
          <p>אין מיילים להצגה</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-right">שולח</th>
                <th className="px-4 py-3 text-right">נושא</th>
                <th className="px-4 py-3 text-right">תיבה</th>
                <th className="px-4 py-3 text-right">סטטוס</th>
                <th className="px-4 py-3 text-right">סיווג AI</th>
                <th className="px-4 py-3 text-right">פניה</th>
                <th className="px-4 py-3 text-right">תאריך</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {emails.map((email) => (
                <tr key={email.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedEmailId(email.id)}>
                  <td className="px-4 py-3">
                    <div className="font-medium truncate max-w-[140px]">{email.from_name || email.from_email}</div>
                    <div className="text-xs text-gray-400 truncate max-w-[140px]">{email.from_name ? email.from_email : ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="line-clamp-1 max-w-[220px]">{email.subject}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[120px]">{email.inbox_email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLOR[email.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {email.status_display}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {email.ai_result ? (
                      <span className="text-green-600">
                        {AI_MATCHED_BY_LABELS[(email.ai_result as Record<string, unknown>).matched_by as string] ?? (email.ai_result as Record<string, unknown>).matched_by as string ?? 'AI'}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {email.service_call_id ? (
                      <a
                        href={`/crm/servicecall?id=${email.service_call_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        #{email.service_call_id}
                      </a>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(email.received_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelectedEmailId(email.id); }}>
                      פרטים
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="text-sm text-gray-600">עמוד {page} מתוך {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Email detail modal */}
      {selectedEmailId !== null && (
        <EmailDetailModal
          emailId={selectedEmailId}
          open={selectedEmailId !== null}
          onOpenChange={(v) => { if (!v) setSelectedEmailId(null); }}
        />
      )}
    </div>
  );
}
